import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Loader,
  SimpleGrid,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { notifications } from '@mantine/notifications'
import { Edit2, ExternalLink, GripVertical, Plus, Search, Trash2 } from 'lucide-react'
import { createService, deleteService, getServices, reorderServices } from '../api'
import ConfirmModal from '../components/ConfirmModal'

export default function ServicesPage() {
  const navigate = useNavigate()
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setServices(await getServices())
    } catch (err) {
      notifications.show({ color: 'red', title: 'Ошибка', message: err.message })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const svc = await createService({
        slug: `new-service-${Date.now()}`,
        title: 'Новая услуга',
        description: '',
        order: services.length,
      })
      navigate(`/admin/services/${svc._id}`)
    } catch (err) {
      notifications.show({ color: 'red', title: 'Ошибка', message: err.message })
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteService(deleteTarget._id)
      notifications.show({ color: 'green', message: `«${deleteTarget.title}» удалена` })
      setDeleteTarget(null)
      load()
    } catch (err) {
      notifications.show({ color: 'red', title: 'Ошибка', message: err.message })
    } finally {
      setDeleting(false)
    }
  }

  const handleDragEnd = async ({ destination, source }) => {
    if (!destination || destination.index === source.index) return
    const reordered = Array.from(services)
    const [moved] = reordered.splice(source.index, 1)
    reordered.splice(destination.index, 0, moved)
    setServices(reordered)
    try {
      await reorderServices(reordered.map((s, i) => ({ id: s._id, order: i })))
    } catch {
      load()
    }
  }

  const filtered = services.filter((s) =>
    s.title.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Title order={3}>Услуги</Title>
        <Button leftSection={<Plus size={16} />} onClick={handleCreate} loading={creating}>
          Добавить
        </Button>
      </Group>

      <TextInput
        placeholder="Поиск по названию..."
        leftSection={<Search size={16} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        mb="lg"
        maw={340}
      />

      {loading ? (
        <Loader />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="services">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                <SimpleGrid cols={1} spacing="sm">
                  {filtered.map((svc, index) => (
                    <Draggable key={svc._id} draggableId={svc._id} index={index}>
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          withBorder
                          radius="md"
                          p="sm"
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.85 : 1,
                            boxShadow: snapshot.isDragging ? '0 4px 20px rgba(0,0,0,.12)' : undefined,
                          }}
                        >
                          <Group gap="sm" wrap="nowrap">
                            <Box
                              {...provided.dragHandleProps}
                              style={{ cursor: 'grab', color: '#adb5bd', flexShrink: 0 }}
                            >
                              <GripVertical size={18} />
                            </Box>

                            {svc.heroImage ? (
                              <Image
                                src={svc.heroImage}
                                h={52}
                                w={80}
                                radius="sm"
                                style={{ objectFit: 'cover', flexShrink: 0 }}
                              />
                            ) : (
                              <Box
                                h={52}
                                w={80}
                                bg="gray.1"
                                style={{ borderRadius: 6, flexShrink: 0 }}
                              />
                            )}

                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text fw={600} truncate>
                                {svc.title}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {svc.slug}
                              </Text>
                            </Box>

                            <Badge variant="light" color="blue" size="sm">
                              {svc.subServicesCount ?? 0} подуслуг
                            </Badge>

                            <Group gap={6} wrap="nowrap">
                              <ActionIcon
                                variant="light"
                                onClick={() => navigate(`/admin/services/${svc._id}`)}
                                title="Редактировать"
                              >
                                <Edit2 size={15} />
                              </ActionIcon>
                              <ActionIcon
                                variant="light"
                                component="a"
                                href={`/services/${svc.slug}`}
                                target="_blank"
                                title="Открыть на сайте"
                              >
                                <ExternalLink size={15} />
                              </ActionIcon>
                              <ActionIcon
                                variant="light"
                                color="red"
                                onClick={() => setDeleteTarget(svc)}
                                title="Удалить"
                              >
                                <Trash2 size={15} />
                              </ActionIcon>
                            </Group>
                          </Group>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                </SimpleGrid>
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <ConfirmModal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Удалить услугу"
        message={`Удалить «${deleteTarget?.title}»? Все подуслуги также будут удалены.`}
      />
    </Box>
  )
}
